import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
    Search,
    ScrollText,
    LogOut,
    ShieldCheck,
    Trash2,
    AlertTriangle,
    Loader2,
    Filter,
    Receipt,
    ArrowDownUp,
    Users,
    ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-GB");
    } catch {
        return iso;
    }
}

export default function AdminInvoices() {
    const { company, logout } = useAuth();
    const navigate = useNavigate();
    const { t } = useLang();

    const [search, setSearch] = useState("");
    const [actionType, setActionType] = useState("all"); // all | new | renewal
    const [sortBy, setSortBy] = useState("date"); // date | company | price
    const [sortDir, setSortDir] = useState("desc"); // asc | desc
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    // Delete dialog state
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deletePassword, setDeletePassword] = useState("");
    const [deleteError, setDeleteError] = useState("");
    const [deleting, setDeleting] = useState(false);

    const fetchInvoices = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search.trim()) params.set("search", search.trim());
            if (actionType !== "all") params.set("action_type", actionType);
            params.set("sort_by", sortBy);
            params.set("sort_dir", sortDir);
            const { data } = await api.get(`/admin/invoices?${params.toString()}`);
            setItems(Array.isArray(data) ? data : []);
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || err.message;
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    }, [search, actionType, sortBy, sortDir]);

    // Refetch whenever filters change OR search changes (debounced).
    useEffect(() => {
        const id = setTimeout(fetchInvoices, 250);
        return () => clearTimeout(id);
    }, [fetchInvoices]);

    const totalRevenue = useMemo(
        () => items.reduce((acc, it) => acc + Number(it.price || 0), 0),
        [items],
    );

    const handleLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    const openDelete = (invoice) => {
        setDeleteTarget(invoice);
        setDeletePassword("");
        setDeleteError("");
    };

    const closeDelete = () => {
        setDeleteTarget(null);
        setDeletePassword("");
        setDeleteError("");
        setDeleting(false);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleteError("");
        if (!deletePassword) {
            setDeleteError(t.passwordRequired);
            return;
        }
        setDeleting(true);
        try {
            await api.delete(`/admin/invoices/${deleteTarget.id}`, {
                data: { password: deletePassword },
            });
            toast.success(t.invoicesDeletedToast);
            // Optimistic update — remove from current list.
            setItems((prev) => prev.filter((x) => x.id !== deleteTarget.id));
            closeDelete();
        } catch (err) {
            const msg = formatApiError(err.response?.data?.detail) || err.message;
            setDeleteError(msg);
            toast.error(msg);
            setDeleting(false);
        }
    };

    return (
        <div className="min-h-screen app-bg">
            <header className="sticky top-0 z-30 border-b border-neutral-200/70 bg-white/85 backdrop-blur">
                <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-2 px-3 sm:h-16 sm:px-6 lg:px-8">
                    <div className="flex min-w-0 items-center gap-2 sm:gap-2.5">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-neutral-900 text-white sm:h-9 sm:w-9 sm:rounded-xl">
                            <ScrollText className="h-4 w-4 sm:h-5 sm:w-5" />
                        </div>
                        <div className="min-w-0">
                            <div className="truncate font-heading text-sm font-bold leading-none sm:text-base">{t.brand}</div>
                            <div className="flex items-center gap-1 truncate text-[11px] text-neutral-500 sm:text-xs">
                                <ShieldCheck className="h-3 w-3 text-amber-700" />
                                {company?.company_name}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-1">
                        <Button
                            variant="ghost"
                            onClick={() => navigate("/admin/accounts")}
                            className="h-9 px-2.5 text-neutral-700 hover:bg-amber-50 hover:text-amber-800 sm:px-3"
                            data-testid="admin-invoices-back-accounts-btn"
                        >
                            <Users className="h-4 w-4 sm:ms-1" />
                            <span className="hidden sm:inline">{t.accountsLogTitle}</span>
                        </Button>
                        <Button
                            variant="ghost"
                            onClick={handleLogout}
                            className="h-9 px-2.5 text-neutral-700 hover:bg-red-50 hover:text-red-700 sm:px-3"
                            data-testid="admin-invoices-logout-btn"
                        >
                            <LogOut className="h-4 w-4 sm:ms-1" />
                            <span className="hidden sm:inline">{t.navLogout}</span>
                        </Button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-7xl px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
                <div className="mb-4 sm:mb-6">
                    <button
                        type="button"
                        onClick={() => navigate("/admin/accounts")}
                        className="inline-flex items-center gap-1 text-sm font-medium text-neutral-600 hover:text-neutral-900"
                        data-testid="admin-invoices-back-link"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        {t.accountsLogTitle}
                    </button>
                    <p className="mt-2 text-[11px] font-semibold uppercase tracking-wider text-amber-700 sm:text-sm">{t.invoicesKicker}</p>
                    <h1 className="font-heading text-2xl font-bold sm:text-3xl lg:text-4xl">{t.invoicesTitle}</h1>
                    <p className="mt-1 text-xs text-neutral-500 sm:text-sm">{t.invoicesSubtitle}</p>
                </div>

                {/* Stats row */}
                <div className="mb-4 grid grid-cols-2 gap-3 sm:max-w-md">
                    <div className="rounded-xl border border-neutral-200 bg-white px-4 py-3 shadow-sm" data-testid="invoices-count-card">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">{t.invoicesCountChip}</div>
                        <div className="mt-1 font-numbers text-2xl font-bold text-neutral-900">{items.length}</div>
                    </div>
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3 shadow-sm" data-testid="invoices-revenue-card">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">{t.invoicesTotalRevenue}</div>
                        <div className="mt-1 font-numbers text-2xl font-bold text-emerald-900">${totalRevenue.toFixed(2)}</div>
                    </div>
                </div>

                {/* Search + Filters */}
                <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="invoices-filters">
                    <Filter className="h-4 w-4 text-neutral-400" />
                    {[
                        ["all", t.invoicesFilterAll],
                        ["new", t.invoicesFilterNew],
                        ["renewal", t.invoicesFilterRenewal],
                    ].map(([k, label]) => (
                        <button
                            key={k}
                            type="button"
                            onClick={() => setActionType(k)}
                            className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                                actionType === k
                                    ? "bg-neutral-900 text-white"
                                    : "border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                            }`}
                            data-testid={`invoices-filter-${k}`}
                        >
                            {label}
                        </button>
                    ))}
                    <span className="mx-2 hidden h-4 w-px bg-neutral-200 sm:block" />
                    <ArrowDownUp className="h-4 w-4 text-neutral-400" />
                    <span className="text-xs font-semibold text-neutral-500">{t.invoicesSortBy}:</span>
                    {[
                        ["date", t.invoicesSortDate],
                        ["company", t.invoicesSortCompany],
                        ["price", t.invoicesSortPrice],
                    ].map(([k, label]) => (
                        <button
                            key={k}
                            type="button"
                            onClick={() => setSortBy(k)}
                            className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                                sortBy === k
                                    ? "bg-amber-700 text-white"
                                    : "border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                            }`}
                            data-testid={`invoices-sort-${k}`}
                        >
                            {label}
                        </button>
                    ))}
                    <button
                        type="button"
                        onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")}
                        className="rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs font-semibold text-neutral-700 hover:bg-neutral-50"
                        data-testid="invoices-sort-dir"
                    >
                        {sortDir === "asc" ? "↑ " + t.invoicesSortDirAsc : "↓ " + t.invoicesSortDirDesc}
                    </button>
                </div>

                <div className="mb-6 flex items-center gap-3">
                    <div className="relative w-full max-w-md">
                        <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t.invoicesSearchPlaceholder}
                            className="h-11 ps-3 pe-10"
                            data-testid="invoices-search-input"
                        />
                    </div>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="rounded-2xl border border-neutral-200 bg-white p-10 text-center shadow-sm">
                        <Loader2 className="mx-auto h-6 w-6 animate-spin text-neutral-400" />
                    </div>
                ) : items.length === 0 ? (
                    <div className="rounded-2xl border border-neutral-200 bg-white p-10 text-center shadow-sm" data-testid="invoices-empty">
                        <Receipt className="mx-auto h-10 w-10 text-neutral-300" />
                        <div className="mt-3 font-heading text-base font-bold text-neutral-700">{t.invoicesEmpty}</div>
                        <p className="mt-1 text-xs text-neutral-500">{t.invoicesEmptyHint}</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto rounded-2xl border border-neutral-200 bg-white shadow-sm" data-testid="invoices-table-wrap">
                        <table className="w-full min-w-[680px] text-start text-sm">
                            <thead className="bg-neutral-50 text-[11px] font-bold uppercase tracking-wider text-neutral-600">
                                <tr>
                                    <th className="px-3 py-2.5 text-start">{t.invoicesColNumber}</th>
                                    <th className="px-3 py-2.5 text-start">{t.invoicesColCompany}</th>
                                    <th className="px-3 py-2.5 text-start">{t.invoicesColDate}</th>
                                    <th className="px-3 py-2.5 text-start">{t.invoicesColAction}</th>
                                    <th className="px-3 py-2.5 text-start">{t.invoicesColPlan}</th>
                                    <th className="px-3 py-2.5 text-start">{t.invoicesColPrice}</th>
                                    <th className="px-3 py-2.5 text-end">{t.invoicesColActions}</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-100">
                                {items.map((inv) => (
                                    <tr key={inv.id} data-testid={`invoice-row-${inv.invoice_number}`}>
                                        <td className="px-3 py-2 font-numbers font-bold text-neutral-900">#{inv.invoice_number}</td>
                                        <td className="px-3 py-2 font-medium text-neutral-800">{inv.company_name}</td>
                                        <td className="px-3 py-2 font-numbers text-xs text-neutral-600">{formatDate(inv.date)}</td>
                                        <td className="px-3 py-2">
                                            {inv.action_type === "renewal" ? (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold text-sky-800 ring-1 ring-sky-300">
                                                    {t.invoicesActionRenewal}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800 ring-1 ring-emerald-300">
                                                    {t.invoicesActionNew}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-xs text-neutral-600">
                                            {inv.plan === "yearly" ? t.invoicesPlanYearly : inv.plan === "monthly" ? t.invoicesPlanMonthly : "—"}
                                        </td>
                                        <td className="px-3 py-2 font-numbers font-bold text-neutral-900" dir="ltr">${Number(inv.price || 0).toFixed(2)}</td>
                                        <td className="px-3 py-2 text-end">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => openDelete(inv)}
                                                className="h-8 border-red-300 text-red-700 hover:bg-red-50"
                                                data-testid={`invoice-delete-btn-${inv.invoice_number}`}
                                            >
                                                <Trash2 className="me-1 h-3.5 w-3.5" />
                                                {t.invoicesDeleteBtn}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </main>

            {/* Delete confirmation */}
            <AlertDialog
                open={!!deleteTarget}
                onOpenChange={(v) => { if (!v) closeDelete(); }}
            >
                <AlertDialogContent dir={t.dir} data-testid="invoice-delete-dialog">
                    <AlertDialogHeader className="text-start">
                        <AlertDialogTitle className="flex items-center gap-2 text-red-800">
                            <AlertTriangle className="h-5 w-5" />
                            {t.invoicesDeleteConfirmTitle}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {t.invoicesDeleteConfirmBody}
                            {deleteTarget ? (
                                <span className="mt-2 block rounded-md bg-neutral-100 px-3 py-1.5 text-xs text-neutral-700">
                                    <span className="font-bold">#{deleteTarget.invoice_number}</span> — {deleteTarget.company_name} — <span className="font-numbers" dir="ltr">${Number(deleteTarget.price || 0).toFixed(2)}</span>
                                </span>
                            ) : null}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="mt-2">
                        <Label className="mb-1.5 block text-sm font-medium">{t.invoicesDeleteOwnerPwLabel}</Label>
                        <Input
                            type="password"
                            value={deletePassword}
                            onChange={(e) => setDeletePassword(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); confirmDelete(); } }}
                            className="h-11"
                            placeholder="••••••••"
                            autoFocus
                            data-testid="invoice-delete-password-input"
                        />
                        {deleteError ? (
                            <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="invoice-delete-error">
                                {deleteError}
                            </div>
                        ) : null}
                    </div>
                    <AlertDialogFooter className="flex-row-reverse gap-2 sm:flex-row-reverse">
                        <AlertDialogCancel disabled={deleting} data-testid="invoice-delete-cancel-btn">
                            {t.cancel}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => { e.preventDefault(); confirmDelete(); }}
                            disabled={deleting}
                            className="bg-red-600 text-white hover:bg-red-700"
                            data-testid="invoice-delete-confirm-btn"
                        >
                            {deleting ? <Loader2 className="me-1 h-4 w-4 animate-spin" /> : <Trash2 className="me-1 h-4 w-4" />}
                            {t.invoicesDeleteConfirmOk}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
